import React, { useState, useEffect } from "react";

// Logo Component accepting width and height props.
function Logo({ width = "100vw", height = "100vh" }) {
  // States used to trigger each animation phase:
  const [showTriangle, setShowTriangle] = useState(false);
  const [drawArc, setDrawArc] = useState(false);
  const [showRedDot, setShowRedDot] = useState(false);

  // Configuration constants (matching your SwiftUI code)
  const triangleSize = 100;
  const circleSize = 130;
  const redDotSize = 30;
  const arcFraction = 0.9;
  const arcRotation = -27; // degrees
  const redDotMultiplier = 1.1;

  // Calculate circle values for the arc drawing effect.
  const radius = circleSize / 2;
  const circumference = 2 * Math.PI * radius;
  const targetDashOffset = circumference * (1 - arcFraction);
  const currentDashOffset = drawArc ? targetDashOffset : circumference;

  // Calculate the red dot position at 45° from center.
  const angleRad = (45 * Math.PI) / 180;
  const offset = radius * redDotMultiplier;
  const redDotX = offset * Math.cos(angleRad);
  const redDotY = -offset * Math.sin(angleRad); // negative y moves upward

  // Apply additional shift (in pixels) to move the red dot top left.
  const shift = 15; // Change this value to adjust the shift
  const finalRedDotX = redDotX - shift;
  const finalRedDotY = redDotY - shift;

  // Sequence the animations similar to SwiftUI's onAppear
  useEffect(() => {
    // 1. Immediately show triangle
    setShowTriangle(true);

    // 2. After 300ms, trigger the arc drawing animation.
    const arcTimeout = setTimeout(() => {
      setDrawArc(true);
    }, 300);

    // 3. After 800ms, show the red dot.
    const redDotTimeout = setTimeout(() => {
      setShowRedDot(true);
    }, 800);

    return () => {
      clearTimeout(arcTimeout);
      clearTimeout(redDotTimeout);
    };
  }, []);

  // Use the passed width and height for the container style.
  const containerStyle = {
    ...styles.container,
    width: width,
    height: height,
  };

  return (
    <div style={containerStyle}>
      {/* Background gradient */}
      <div style={styles.background}></div>

      {/* Overlay container for animated elements */}
      <div style={styles.overlay}>
        {/* Triangle (Play Button) */}
        <svg
          width={triangleSize}
          height={triangleSize}
          style={{
            ...styles.triangle,
            opacity: showTriangle ? 1 : 0,
            transition: "opacity 0.8s ease-in",
          }}
        >
          <polygon points={getTrianglePoints(triangleSize)} fill="white" />
        </svg>

        {/* Arc (Incomplete Circle) */}
        <svg
          width={circleSize}
          height={circleSize}
          viewBox={`-2.5 -2.5 ${circleSize + 5} ${circleSize + 5}`}
          style={{
            ...styles.arc,
            transform: `rotate(${arcRotation}deg)`,
            overflow: "visible",
          }}
        >
          <circle
            cx={radius}
            cy={radius}
            r={radius}
            fill="none"
            stroke="white"
            strokeWidth="5"
            strokeDasharray={circumference}
            strokeDashoffset={currentDashOffset}
            style={{
              transition: drawArc
                ? "stroke-dashoffset 1s cubic-bezier(0.5, 1, 0.89, 1)"
                : "none",
            }}
          />
        </svg>

        {/* Red Dot */}
        <div
          style={{
            ...styles.redDot,
            width: redDotSize,
            height: redDotSize,
            backgroundColor: "red",
            borderRadius: "50%",
            opacity: showRedDot ? 1 : 0,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: `translate(${finalRedDotX}px, ${finalRedDotY}px)`,
            transition: "opacity 0.5s ease-out 0.8s",
          }}
        ></div>
      </div>
    </div>
  );
}

// Helper function to generate triangle points.
function getTrianglePoints(size) {
  const inset = size * 0.2;
  const top = `${inset},${inset}`;
  const bottom = `${inset},${size - inset}`;
  const tip = `${size - inset},${size / 2}`;
  return `${top} ${bottom} ${tip}`;
}

// Default inline styles.
const styles = {
  container: {
    position: "relative",
    overflow: "hidden",
  },
  background: {
    position: "absolute",
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom right, #5a6df9, #bf2fea)",
    zIndex: -1,
  },
  overlay: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  triangle: {
    position: "absolute",
    top: "calc(50% - 50px)", // centers triangle based on triangleSize / 2
    left: "calc(50% - 50px)",
    zIndex: 1,
  },
  arc: {
    position: "absolute",
    top: "calc(50% - 65px)", // centers arc based on circleSize / 2
    left: "calc(50% - 65px)",
    zIndex: 1,
  },
  redDot: {
    zIndex: 2,
  },
};

export default Logo;
